import React, { useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthProvider";
import { Heading } from "../heading";
import { ACCENT } from "../cuentas/shared";
import { useStickyMaxHeight } from "../../hooks/useStickyMaxHeight";
import { REPORTES_ADMIN, reporteAdminPorId } from "./registryAdmin";
import { ShieldCheckIcon } from "@heroicons/react/20/solid";

// Los reportes de Venueboard, no los de un venue. Viven aparte porque miran
// otra cosa: cruzan TODOS los venues y contestan preguntas de operación del
// negocio (quién usa el panel, quién se está enfriando), no del venue que
// el admin trae seleccionado. Meterlos en Reportes los volvía un caso raro
// dentro de una pantalla que siempre habla de un solo venue.
//
// Misma forma que Reportes —índice a la izquierda, reporte a la derecha—
// para que se navegue igual.
const ReportesAdminPage = () => {
  const { userInfo } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const indiceRef = useRef(null);
  useStickyMaxHeight(indiceRef, { top: 16, modo: "fluir" });

  const seleccionado = useMemo(
    () => reporteAdminPorId(searchParams.get("r")) || REPORTES_ADMIN[0],
    [searchParams]
  );

  const elegir = (id) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("r", id);
      return next;
    }, { replace: true });

  // La ruta ya está detrás de AdminRoute; esto es el segundo candado, por si
  // alguien llega por otro camino.
  if (userInfo?.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto p-6 text-sm text-zinc-500">
        Solo para administradores.
      </div>
    );
  }

  const { Component, Icon } = seleccionado;

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="mb-5">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-6 w-6" style={{ color: ACCENT }} />
          <Heading level={2}>Reportes admin</Heading>
        </div>
        <p className="text-sm text-zinc-500 mt-1">
          Reportes de la plataforma, no de un venue: cruzan todos los venues y
          solo los ve el equipo de Venueboard.
        </p>
      </div>

      <div className="flex gap-8">
        <aside className="hidden lg:block w-60 shrink-0">
          <nav ref={indiceRef} className="sticky top-4 pr-1 space-y-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">
              Plataforma
            </div>
            <ul className="space-y-0.5">
              {REPORTES_ADMIN.map((r) => {
                const activo = r.id === seleccionado.id;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => elegir(r.id)}
                      aria-current={activo ? "page" : undefined}
                      className={`w-full text-left flex items-start gap-2 px-2 py-1 rounded-md transition-colors ${
                        activo
                          ? "bg-[#1b4d2e]/10 text-[#1b4d2e] dark:text-emerald-300"
                          : "text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                      }`}
                    >
                      <r.Icon
                        className={`h-4 w-4 mt-0.5 shrink-0 ${activo ? "" : "text-zinc-400"}`}
                        style={activo ? { color: ACCENT } : undefined}
                      />
                      <span className="min-w-0 flex-1 leading-tight">
                        <span className={`block truncate text-sm ${activo ? "font-semibold" : ""}`}>
                          {r.titulo}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          {REPORTES_ADMIN.length > 1 && (
            <div className="lg:hidden mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1">
                Reporte
              </label>
              <select
                value={seleccionado.id}
                onChange={(e) => elegir(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
              >
                {REPORTES_ADMIN.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.titulo}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm mb-6 overflow-hidden">
            <div className="flex items-start gap-3 p-5">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: ACCENT + "1a" }}
              >
                <Icon className="h-5 w-5" style={{ color: ACCENT }} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                  {seleccionado.titulo}
                </h2>
                <p className="text-sm text-zinc-500 mt-0.5">
                  {seleccionado.descripcion}
                </p>
              </div>
            </div>
          </div>

          <Component />
        </main>
      </div>
    </div>
  );
};

export default ReportesAdminPage;
